import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

interface Source {
  /** i18n key suffix under pages.useCases.memoryKeeping.problem.sources. */
  key: string;
  accent: string;
  icon: ReactNode;
  /** Absolute position + rotation + float delay for the scattered layout. */
  pos: string;
  rotate: string;
  delay: string;
}

const IMAGE = (
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <circle cx="8.5" cy="9" r="1.5" />
    <path d="m4 18 5-5 4 4 3-3 4 4" />
  </>
);
const LINK = (
  <>
    <path d="M9 14a4 4 0 0 1 0-6l2-2a4 4 0 1 1 6 6l-1 1" />
    <path d="M15 10a4 4 0 0 1 0 6l-2 2a4 4 0 1 1-6-6l1-1" />
  </>
);

const SOURCES: Source[] = [
  {
    key: 'camera',
    accent: '#6ed7a6',
    icon: IMAGE,
    pos: 'left-0 top-[10px]',
    rotate: '-4deg',
    delay: '0s',
  },
  {
    key: 'messages',
    accent: '#7fa8ff',
    icon: (
      <>
        <path d="M4 5h16v11H8l-4 3z" />
        <path d="M8 9h8M8 12h5" />
      </>
    ),
    pos: 'left-[55px] top-[130px]',
    rotate: '3deg',
    delay: '1.5s',
  },
  {
    key: 'voice',
    accent: '#FDE47A',
    icon: (
      <>
        <rect x="9" y="3" width="6" height="12" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      </>
    ),
    pos: 'left-[205px] top-[25px]',
    rotate: '5deg',
    delay: '0.8s',
  },
  {
    key: 'notes',
    accent: '#cbd5e1',
    icon: <path d="M6 5h12M6 9h12M6 13h8M6 17h10" />,
    pos: 'left-[255px] top-[185px]',
    rotate: '-3deg',
    delay: '2s',
  },
  {
    key: 'email',
    accent: '#f5a3c2',
    icon: (
      <>
        <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    pos: 'left-[15px] top-[265px]',
    rotate: '2deg',
    delay: '1.2s',
  },
  {
    key: 'physical',
    accent: '#ffb27a',
    icon: IMAGE,
    pos: 'left-[155px] top-[320px]',
    rotate: '-6deg',
    delay: '0.4s',
  },
  {
    key: 'documents',
    accent: '#b69bff',
    icon: (
      <>
        <rect x="5" y="4" width="14" height="16" rx="2" />
        <path d="M9 9h6M9 13h6M9 17h4" />
      </>
    ),
    pos: 'left-[5px] top-[420px]',
    rotate: '3.5deg',
    delay: '1.8s',
  },
  {
    key: 'links',
    accent: '#7fd0ff',
    icon: LINK,
    pos: 'left-[200px] top-[415px]',
    rotate: '-2deg',
    delay: '0.9s',
  },
  {
    key: 'todo',
    accent: '#ffd27a',
    icon: <path d="m5 12 5 5L20 7" />,
    pos: 'left-[85px] top-[510px]',
    rotate: '4deg',
    delay: '2.5s',
  },
];

export const Section27 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            {t('pages.useCases.memoryKeeping.problem.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.useCases.memoryKeeping.problem.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h2>
          <div className={styles.lead}>
            <p>{t('pages.useCases.memoryKeeping.problem.line1')}</p>
            <p>{t('pages.useCases.memoryKeeping.problem.line2')}</p>
            <p>{t('pages.useCases.memoryKeeping.problem.line3')}</p>
            <p className={styles.leadGap}>
              {t.rich('pages.useCases.memoryKeeping.problem.line4', {
                strong: (chunks) => (
                  <span className={styles.leadStrong}>{chunks}</span>
                ),
              })}
            </p>
            <p>{t('pages.useCases.memoryKeeping.problem.line5')}</p>
          </div>
        </div>

        <div className={styles.scatterWrap}>
          <div className={styles.scatter}>
            {SOURCES.map((s) => (
              <div
                key={s.key}
                className={`${styles.card} ${s.pos} motion-safe:animate-floaty`}
                style={{ rotate: s.rotate, animationDelay: s.delay }}
              >
                <span
                  className={styles.cardIcon}
                  style={{ background: `${s.accent}24`, color: s.accent }}
                >
                  <svg viewBox="0 0 24 24" className={styles.glyph}>
                    {s.icon}
                  </svg>
                </span>
                <span>
                  <span className={styles.cardLabel}>
                    {t(
                      `pages.useCases.memoryKeeping.problem.sources.${s.key}.label`,
                    )}
                  </span>
                  <span className={styles.cardCount}>
                    {t(
                      `pages.useCases.memoryKeeping.problem.sources.${s.key}.count`,
                    )}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10',
  copy: 'text-center lg:text-left',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'mx-auto max-w-[560px] font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px] lg:mx-0',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-8 max-w-[560px] text-[22px] leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-[24px]',
  leadGap: 'mt-[18px]',
  leadStrong: 'font-semibold text-mf-off-black-600 marketing-dark:text-white',
  scatterWrap: 'flex justify-center lg:block',
  scatter: 'relative h-[600px] w-[400px]',
  card: classNames(
    'absolute flex min-w-[180px] items-center gap-3 rounded-[14px] border px-4 py-3',
    'border-[#F0F0F0] bg-white shadow-[0_8px_22px_rgba(20,30,60,0.06)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f]',
    'marketing-dark:shadow-[0_12px_28px_-12px_rgba(0,0,0,0.5)]',
  ),
  cardIcon:
    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px]',
  glyph: 'h-[18px] w-[18px] fill-none stroke-current stroke-2',
  cardLabel:
    'block text-sm font-bold text-mf-off-black-600 marketing-dark:text-white',
  cardCount: 'block text-xs text-mf-gray-500 marketing-dark:text-white/50',
};
